import React, { useState } from "react";
import { AutocompleteProps } from "@/components/ui/autocomplete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Icon } from "@iconify/react";
import { useTranslations } from "use-intl";
export type BaseEnumResolverProps = {
  onValue: (option: {
    value: string;
    label: string;
  } | {
    value: string;
    label: string;
  }[]) => void;
  options: {
    value: string;
    label: string;
  }[];
  initialValue?: string;
  useToggleForTwoOptions?: boolean;
  /** @description for now it works only if `useToggleForTwoOptions` is checked */
  isOptional?: boolean;
} & Omit<AutocompleteProps, "options" | "onChange">;
type ToggleButtonsProps = {
  options: {
    value: string;
    label: string;
  }[];
  initialValue?: string;
  onValue: (option: {
    value: string;
    label: string;
  }) => void;
};
const getIcon = (value: string) => {
  switch (value) {
    case "DEPARTMENT":
      return "mdi:office-building";
    case "PUBLIC_SERVANT":
      return "mdi:account-tie";
    case "DOCUMENT":
      return "mdi:file-document-outline";
    case "FORM":
      return "mdi:form-select";
    case "PUBLIC":
      return "mdi:account-group";
    case "PRIVATE":
      return "mdi:account";
    case "CITIZEN":
      return "mdi:account";
    case "LEGAL_ENTITY":
      return "mdi:account-cash";
    case "YES":
      return "mdi:check";
    case "NO":
      return "mdi:close";
    case "PROCEDURE_FINISHED":
      return "mdi:check-circle";
    case "PROCEDURE_STARTED":
      return "mdi:play-circle";
    default:
      return null;
  }
};
const ToggleButtons: React.FC<ToggleButtonsProps> = ({
  options,
  initialValue,
  onValue
}) => {
  const [selected, setSelected] = useState<string | undefined>(initialValue);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, value: string) => {
    event.preventDefault();
    event.stopPropagation();
    if (selected !== value) {
      setSelected(value);
      onValue({
        value,
        label: options.find(option => option.value === value)?.label ?? ""
      });
    } else {
      setSelected(undefined);
      onValue({
        value: "",
        label: ""
      });
    }
  };
  return <Card className="inline-flex w-full border border-input" role="group" data-sentry-element="Card" data-sentry-component="ToggleButtons" data-sentry-source-file="BaseEnumResolver.tsx">
      {options.map((option, index) => {
      const isFirst = index === 0;
      const isLast = index === options.length - 1;
      const icon = getIcon(option.value);
      return <button key={option.value} type="button" className={`flex w-full items-center justify-center gap-3 px-6 py-3.5 text-sm font-medium text-foreground transition-all focus:z-10 focus:ring-0 
              ${isFirst ? "rounded-l-3xl" : ""}
              ${isLast ? "rounded-r-3xl" : ""}
              ${!isFirst && !isLast ? "border-l-0" : ""}
              ${selected === option.value ? "bg-blue-800/80 text-white hover:bg-blue-800" : "bg-gray-500/10 hover:bg-gray-500/20"}
            `} onClick={event => handleClick(event, option.value)}>
            {icon && <Icon icon={icon} className="h-5 w-5" />}
            <span>{option.label}</span>
          </button>;
    })}
    </Card>;
};
export const BaseEnumResolver: React.FC<BaseEnumResolverProps> = ({
  onValue,
  options,
  initialValue,
  useToggleForTwoOptions = false,
  isOptional,
  ...props
}) => {
  const t = useTranslations();
  if (useToggleForTwoOptions && options.length === 2) {
    return <ToggleButtons options={options} initialValue={initialValue} onValue={onValue} />;
  }
  return <Select disabled={props.disabled} value={initialValue || undefined} onValueChange={val => {
    onValue({
      value: val,
      label: options.find(option => option.value === val)?.label ?? ""
    });
  }} data-sentry-element="Select" data-sentry-component="BaseEnumResolver" data-sentry-source-file="BaseEnumResolver.tsx">
      <SelectTrigger className="min-w-[80px]" data-sentry-element="SelectTrigger" data-sentry-source-file="BaseEnumResolver.tsx">
        <SelectValue placeholder={t("placeholder_chooseEllipsis_20230807143001")} data-sentry-element="SelectValue" data-sentry-source-file="BaseEnumResolver.tsx" />
      </SelectTrigger>
      <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="BaseEnumResolver.tsx">
        {options.map(option => <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>)}
      </SelectContent>
    </Select>;
};